import * as Yup from 'yup';
import React, { useState } from 'react';
import { Formik } from 'formik-v2';
import { ActionButton } from '@ax2/common/components/ActionButton';
import TextField from './form/TextField';
import formStyles from '../styles/components/form/Forms.module.css';
import { requestPasswordReset } from '../rest/passwordResetClient';
import PasswordResetRequest from '../model/password-reset/PasswordResetRequest';

interface ForgotPasswordFormValues {
  email: string;
}

const forgotPasswordSchema = Yup.object<ForgotPasswordFormValues>().shape({
  email: Yup.string()
    .email('Must be a valid email')
    .required('Email is required'),
});

export interface ForgotPasswordFormProps {
  onComplete?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onComplete,
}) => {
  const [complete, setComplete] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const initialValues: ForgotPasswordFormValues = {
    email: '',
  };

  if (complete) {
    return (
      <div className={formStyles.forgotPasswordText}>
        Password reset instructions were sent to the provided email address.
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={forgotPasswordSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        requestPasswordReset(new PasswordResetRequest(values.email))
          .then(() => {
            setSubmitting(false);
            setComplete(true);
            onComplete && onComplete();
          })
          .catch(e => {
            setSubmitting(false);
            if (e.reason && e.reason === 'ClientNotFound') {
              setFormError('This email address was not found');
            } else {
              setFormError(e.description || e.message || e.toString());
            }
          });
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit} className={formStyles.form}>
          <div className={formStyles.formItems}>
            <TextField
              name="email"
              label="Email"
              labelNote="required"
              wrapperClassName={formStyles.formItemShorterWidth}
            />
            {formError ? (
              <div className={formStyles.formError}>{formError}</div>
            ) : null}
            <div className={formStyles.formButtons}>
              <ActionButton
                onClick={() => !isSubmitting && handleSubmit()}
                loading={isSubmitting}
                type="primary"
                text="Restore Password"
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};
